import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  HotelIngredient,
  OwnStayIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { HotelFiltersForm } from '@apps/main/new-desktop/modules/hotelFilters/model/hotelFilters';
import { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters/model/hotelFilters';

import type {
  DestinationProduct,
  MoneyRange,
} from '@libs/trip-planner-interfaces';
import type { AppCheckboxListOption } from '@libs/ui';

import type {
  HotelSelectionClientFilters,
  HotelSelectionFilterCandidate,
  HotelSelectionFilterOption,
  HotelSelectionPriceBounds,
  HotelSelectionStarRating,
} from '../../models/hotelSelectionFilters';
import {
  HOTEL_SELECTION_ANY_STAR_CHIP,
  HOTEL_SELECTION_POPULAR_FILTERS,
  HOTEL_SELECTION_PRICE_STEP,
  HOTEL_SELECTION_UNFILTERED_STAR_RATING,
} from '../../models/hotelSelectionFilters';

const { t } = i18n.global;

function getProductFilterCandidate(
  product: DestinationProduct
): HotelSelectionFilterCandidate {
  const rating = product.guestRatings?.overall;

  return {
    hotelCode: product.hotelCode,
    totalPrice: product.totalPrice?.amount,
    guestRatingOverTen: rating ? rating * 2 : undefined,
  };
}

function getMaximumLoadedCandidateTotalPrice(
  candidates: HotelSelectionFilterCandidate[]
): number {
  return candidates.reduce((maximum, candidate) => {
    const totalPrice = candidate.totalPrice;
    return totalPrice !== undefined && Number.isFinite(totalPrice)
      ? Math.max(maximum, totalPrice)
      : maximum;
  }, 0);
}

function getMinimumLoadedCandidateTotalPrice(
  candidates: HotelSelectionFilterCandidate[]
): number {
  const minimumPrice = candidates.reduce((minimum, candidate) => {
    const totalPrice = candidate.totalPrice;
    return totalPrice !== undefined &&
      Number.isFinite(totalPrice) &&
      totalPrice > 0
      ? Math.min(minimum, totalPrice)
      : minimum;
  }, Number.POSITIVE_INFINITY);

  return Number.isFinite(minimumPrice) ? minimumPrice : 0;
}

function toPriceBounds(
  minimum: number,
  maximum: number
): HotelSelectionPriceBounds {
  return {
    minimum,
    maximum: Math.max(maximum, minimum + HOTEL_SELECTION_PRICE_STEP),
  };
}

export function getPriceBoundsFromRange(
  totalPriceRange: MoneyRange
): HotelSelectionPriceBounds {
  return toPriceBounds(totalPriceRange.from.amount, totalPriceRange.to.amount);
}

export function getPriceBoundsFromCandidates(
  candidates: HotelSelectionFilterCandidate[]
): HotelSelectionPriceBounds {
  return toPriceBounds(
    getMinimumLoadedCandidateTotalPrice(candidates),
    getMaximumLoadedCandidateTotalPrice(candidates)
  );
}

export function filterHotelSelectionProducts(
  products: DestinationProduct[],
  filters: HotelSelectionClientFilters,
  previouslySeenHotelCodes: ReadonlySet<string>
): DestinationProduct[] {
  const visibleHotelCodes = new Set(
    filterHotelSelectionCandidates(
      products.map(getProductFilterCandidate),
      filters,
      previouslySeenHotelCodes
    ).map((candidate) => candidate.hotelCode)
  );

  return products.filter((product) => visibleHotelCodes.has(product.hotelCode));
}

export function filterHotelSelectionCandidates<
  T extends HotelSelectionFilterCandidate,
>(
  candidates: T[],
  filters: HotelSelectionClientFilters,
  previouslySeenHotelCodes: ReadonlySet<string>
): T[] {
  return candidates.filter((candidate) => {
    if (
      filters.previouslySeenOnly &&
      !previouslySeenHotelCodes.has(candidate.hotelCode)
    ) {
      return false;
    }

    if (filters.minimumGuestRating > 0) {
      const guestRating = candidate.guestRatingOverTen;
      if (
        guestRating === undefined ||
        guestRating < filters.minimumGuestRating
      ) {
        return false;
      }
    }

    return true;
  });
}

export function getMealFilterOptions(
  form: HotelFiltersForm
): HotelSelectionFilterOption[] {
  return [
    {
      id: HotelFilter.MealsNoMeals,
      label: t('filter.boardBasis.no_meals'),
      checked: form.meals.noMeals,
    },
    {
      id: HotelFilter.MealsBreakfast,
      label: t('filter.boardBasis.breakfast'),
      checked: form.meals.breakfast,
    },
    {
      id: HotelFilter.MealsHalfBoard,
      label: t('filter.boardBasis.half_board'),
      checked: form.meals.halfBoard,
    },
    {
      id: HotelFilter.MealsFullBoard,
      label: t('filter.boardBasis.full_board'),
      checked: form.meals.fullBoard,
    },
    {
      id: HotelFilter.MealsAllInclusive,
      label: t('filter.boardBasis.all_inclusive'),
      checked: form.meals.allInclusive,
    },
  ];
}

export function getAmenityFilterOptions(
  form: HotelFiltersForm
): HotelSelectionFilterOption[] {
  return [
    {
      id: HotelFilter.AmenitiesWifi,
      label: t('filter.amenities.wifi'),
      checked: form.amenities.wifi,
    },
    {
      id: HotelFilter.AmenitiesAirConditioned,
      label: t('filter.amenities.air_conditioned'),
      checked: form.amenities.airConditioned,
    },
    {
      id: HotelFilter.AmenitiesPool,
      label: t('filter.amenities.pool'),
      checked: form.amenities.pool,
    },
    {
      id: HotelFilter.AmenitiesParking,
      label: t('filter.amenities.parking'),
      checked: form.amenities.parking,
    },
    {
      id: HotelFilter.AmenitiesGym,
      label: t('filter.amenities.gym'),
      checked: form.amenities.gym,
    },
    {
      id: HotelFilter.AmenitiesSpa,
      label: t('filter.amenities.spa'),
      checked: form.amenities.spa,
    },
    {
      id: HotelFilter.AmenitiesVehicleChargingStation,
      label: t('filter.ev_charging'),
      checked: form.amenities.vehicleChargingStation,
    },
  ];
}

export function getPropertyTypeFilterOptions(
  form: HotelFiltersForm
): HotelSelectionFilterOption[] {
  return [
    {
      id: HotelFilter.AccommodationTypeHotel,
      label: t('filter.hotelTypes.hotels', 1),
      checked: form.typeOfAccommodation.hotel,
    },
    {
      id: HotelFilter.AccommodationTypeApartment,
      label: t('filter.hotelTypes.apartments'),
      checked: form.typeOfAccommodation.apartment,
    },
    {
      id: HotelFilter.AccommodationTypeHouseOrCottage,
      label: t('filter.hotelTypes.housesAndCottages'),
      checked: form.typeOfAccommodation.houseOrCottage,
    },
  ];
}

export function getExperienceFilterOptions(
  form: HotelFiltersForm
): HotelSelectionFilterOption[] {
  return [
    {
      id: HotelFilter.ExperienceFamily,
      label: t('filter.travel_experience.family'),
      checked: form.experience.family,
    },
    {
      id: HotelFilter.ExperienceBusiness,
      label: t('filter.travel_experience.business'),
      checked: form.experience.business,
    },
    {
      id: HotelFilter.ExperiencePetsAllowed,
      label: t('filter.travel_experience.pets'),
      checked: form.experience.petsAllowed,
    },
    {
      id: HotelFilter.ExperienceGreenSustainableProperty,
      label: t('filter.travel_experience.sustainable_property'),
      checked: form.experience.greenSustainableProperty,
    },
  ];
}

export function getPopularFilterOptions(
  form: HotelFiltersForm
): HotelSelectionFilterOption[] {
  const options = [
    ...getMealFilterOptions(form),
    ...getAmenityFilterOptions(form),
  ];

  return HOTEL_SELECTION_POPULAR_FILTERS.map((filter) =>
    options.find((option) => option.id === filter)
  ).filter(
    (option): option is HotelSelectionFilterOption => option !== undefined
  );
}

export function getNeighborhoodFilterOptions(
  ingredient: HotelIngredient | OwnStayIngredient | null,
  selectedNeighborhoodIds: string[]
): AppCheckboxListOption<string>[] {
  if (!ingredient || !('neighborhoodPolygons' in ingredient)) {
    return [];
  }

  return [...ingredient.neighborhoodPolygons]
    .sort((first, second) => second.score - first.score)
    .map((neighborhood) => ({
      id: neighborhood.id,
      label: neighborhood.name,
      checked: selectedNeighborhoodIds.includes(neighborhood.id),
    }));
}

export function isStarRatingSelected(
  selectedStarRating: number,
  rating: HotelSelectionStarRating
): boolean {
  return rating === HOTEL_SELECTION_ANY_STAR_CHIP
    ? selectedStarRating === HOTEL_SELECTION_UNFILTERED_STAR_RATING
    : selectedStarRating === rating;
}
